// UsersPage.tsx — Co Marca Admin — estilo CategoryPage
import Loading from '../../components/Loading';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Trash2, Edit, Plus, ChevronRight, Home, ShieldCheck } from 'lucide-react';
import UserModal from '../../components/Users/UserModal';
import EditUserModal from '../../components/Users/EditUserModal';
import DeleteUserModal from '../../components/Users/DeleteUserModal';
import Pagination from '../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../components/Expired/ExpiredSessionModal';
import UserAvatar from '../../components/Users/UserAvatar';
import { getUsers } from '../../services/userService';
import { fetchClientes } from '../../services/clienteService';
import { fetchPersonal } from '../../services/personalService';
import { useSessionExpiration } from '../../hooks/useSessionExpiration';
import { useAuth } from '../../context/AuthContext';
import { useModelPermission } from '../../hooks/useModelPermission';

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.usr-page { font-family: 'DM Sans', sans-serif; }
@keyframes usrFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.usr-fadeup   { animation:usrFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.usr-fadeup-1 { animation:usrFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }
.usr-btn-primary {
    display:inline-flex;align-items:center;gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:9px 20px;font-size:.83rem;font-weight:800;
    cursor:pointer;font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(193, 52, 139,.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);white-space:nowrap;
}
.usr-btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(193, 52, 139,.32);}
.usr-table-row{transition:background .15s;}
.usr-table-row:hover{background:#fafcff;}
.usr-action-btn{width:30px;height:30px;border-radius:9px;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;}
.custom-scroll::-webkit-scrollbar{height:6px;width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background-color:rgba(97, 37, 123,0.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background-color:rgba(97, 37, 123,0.5);}
`;

const fmtDate = (d: string) => {
    if (!d) return 'Nunca';
    try { return new Date(d).toLocaleString('es-ES',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}); } catch { return d; }
};

const UsersPage: React.FC = () => {
    const [isModalOpen, setIsModalOpen]           = useState(false);
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const [isActive, setIsActive]                 = useState(true);
    const [users, setUsers]                       = useState<any[]>([]);
    const [fotoMap, setFotoMap]                   = useState<Record<number, string|null>>({});
    const [currentPage, setCurrentPage]           = useState(1);
    const itemsPerPage = 10;
    const [selectedUser, setSelectedUser]         = useState<any>(null);
    const [isEditModalOpen, setIsEditModalOpen]   = useState(false);
    const [selectedUserForDelete, setSelectedUserForDelete] = useState<any>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const { user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('customuser', userLevelId);

    const loadUsers = useCallback(async () => {
        try {
            if (perm?.can_read) setUsers(await getUsers());
            else setUsers([]);
        } catch { setUsers([]); }
    }, [perm]);

    const loadFotos = useCallback(async () => {
        try {
            const [clientes, personal] = await Promise.all([fetchClientes(), fetchPersonal()]);
            const map: Record<number,string|null> = {};
            clientes.forEach((c: any) => { if (c.usuario?.id != null) map[c.usuario.id] = c.foto_url ?? null; });
            personal.forEach((p: any) => { if (p.usuario?.id != null && !(p.usuario.id in map)) map[p.usuario.id] = p.foto_url ?? null; });
            setFotoMap(map);
        } catch { /* silent */ }
    }, []);

    useEffect(() => { loadUsers(); loadFotos(); }, [loadUsers, loadFotos]);

    const sortedUsers = useMemo(() => [...users].sort((a,b)=>a.id-b.id), [users]);
    const totalPages  = Math.ceil(sortedUsers.length / itemsPerPage);
    const paginatedUsers = sortedUsers.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

    const handleUserCreated  = (u: any) => setUsers(p=>[...p,u]);
    const handleUserUpdated  = (u: any) => setUsers(p=>p.map(x=>x.id===u.id?{...x,...u}:x));
    const handleUserDeleted  = (id: number) => setUsers(p=>p.filter(x=>x.id!==id));

    if (perm === null) return <Loading text="Cargando usuarios..." />;

    return (
        <>
            <style>{css}</style>

            <div className="usr-page" style={{ background:'#fff', minHeight:'100vh', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>

                {/* ── HEADER ── */}
                <div className="usr-fadeup" style={{ marginBottom:'28px' }}>
                    <nav style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'12px' }}>
                        <a href="/dashboard" style={{ color:'#94a3b8', textDecoration:'none', display:'flex', alignItems:'center', gap:'3px', transition:'color .2s' }}
                            onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                            onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                            <Home size={11}/> Dashboard
                        </a>
                        <ChevronRight size={10} color="#e2e8f0"/>
                        <span style={{ color:colors.primary, fontWeight:'700' }}>Usuarios</span>
                    </nav>

                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
                        <div>
                            <h1 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.025em', lineHeight:1.1, marginBottom:'6px' }}>
                                Gestión de <span style={{ color:colors.primary }}>Usuarios</span>
                            </h1>
                            <p style={{ fontSize:'.85rem', color:'#64748b', fontWeight:'500' }}>Administra los usuarios y sus niveles de acceso</p>
                            <div style={{ display:'flex', gap:'5px', marginTop:'10px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'3px', width:i===0?'24px':'8px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        {perm.can_create && (
                            <button className="usr-btn-primary" onClick={()=>setIsModalOpen(true)}>
                                <Plus size={15}/> Agregar Usuario
                            </button>
                        )}
                    </div>
                </div>

                {/* ── TABLA ── */}
                <div className="usr-fadeup-1" style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #f1f5f9', boxShadow:'0 4px 24px rgba(0,0,0,.04)', overflow:'hidden' }}>

                    <ExpiredSessionModal isOpen={isSessionExpired} onClose={()=>{ setIsSessionExpired(false); window.location.href='/login'; }}/>

                    {/* Contador */}
                    <div style={{ padding:'16px 24px', borderBottom:'1.5px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <div style={{ width:'32px', height:'32px', borderRadius:'10px', background:`${colors.primary}12`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <Users size={15} color={colors.primary}/>
                            </div>
                            <span style={{ fontSize:'.82rem', fontWeight:'800', color:'#0f172a' }}>{sortedUsers.length}</span>
                            <span style={{ fontSize:'.78rem', color:'#94a3b8', fontWeight:'500' }}>usuarios registrados</span>
                        </div>
                        <span style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'600' }}>
                            Página {currentPage} de {totalPages||1}
                        </span>
                    </div>

                    {!perm.can_read ? (
                        <div style={{ padding:'60px 24px', textAlign:'center', color:'#94a3b8' }}>
                            <ShieldCheck size={40} style={{ margin:'0 auto 12px', opacity:.3 }}/>
                            <p style={{ fontWeight:'700' }}>No tienes permiso para ver usuarios.</p>
                        </div>
                    ) : (
                        <>
                            <div className="custom-scroll" style={{ overflowX:'auto' }}>
                                <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:"'DM Sans',sans-serif", minWidth:'900px' }}>
                                    <thead>
                                        <tr style={{ background:'#fafcff', borderBottom:'1.5px solid #f1f5f9' }}>
                                            {['Usuario','Email','Nivel','Estado','Penúltimo acceso','Último acceso','Acciones'].map((h,i)=>(
                                                <th key={i} style={{ padding:'12px 18px', textAlign:'left', fontSize:'.68rem', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.08em', whiteSpace:'nowrap' }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedUsers.map(u=>(
                                            <tr key={u.id} className="usr-table-row" style={{ borderBottom:'1px solid #f8fafc' }}>
                                                {/* Usuario */}
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                                                        <UserAvatar firstName={u.first_name} lastName={u.last_name} fotoUrl={fotoMap[u.id]} size="sm"/>
                                                        <span style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a', textTransform:'capitalize' }}>
                                                            {u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : 'N/A'}
                                                        </span>
                                                    </div>
                                                </td>
                                                {/* Email */}
                                                <td style={{ padding:'12px 18px' }}>
                                                    <span style={{ fontSize:'.82rem', color:'#64748b' }}>{u.email}</span>
                                                </td>
                                                {/* Nivel */}
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    <span style={{ display:'inline-flex', alignItems:'center', background:`${colors.secondary}10`, border:`1px solid ${colors.secondary}22`, color:colors.secondary, borderRadius:'6px', padding:'2px 8px', fontSize:'.72rem', fontWeight:'700' }}>
                                                        {u.tipo_nivel_usuario?.nombre || 'Sin nivel'}
                                                    </span>
                                                </td>
                                                {/* Estado */}
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    {u.estado ? (
                                                        <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.secondaryLight}14`, border:`1px solid ${colors.secondaryLight}30`, color:'#065f46', borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                                            <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.secondaryLight, display:'inline-block' }}/>
                                                            Activo
                                                        </span>
                                                    ) : (
                                                        <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.primary}10`, border:`1px solid ${colors.primary}22`, color:colors.primaryDark, borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                                            <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.primary, display:'inline-block' }}/>
                                                            Inactivo
                                                        </span>
                                                    )}
                                                </td>
                                                {/* Penúltimo acceso */}
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    <span style={{ fontSize:'.78rem', color:'#64748b', fontWeight:'500' }}>{fmtDate(u.penultimo_acceso)}</span>
                                                </td>
                                                {/* Último acceso */}
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    <span style={{ fontSize:'.78rem', color:'#64748b', fontWeight:'500' }}>{fmtDate(u.ultimo_acceso)}</span>
                                                </td>
                                                {/* Acciones */}
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    <div style={{ display:'flex', gap:'6px' }}>
                                                        {perm.can_update && (
                                                            <button className="usr-action-btn" onClick={()=>{ setSelectedUser(u); setIsEditModalOpen(true); }} title="Editar"
                                                                style={{ background:`${colors.secondary}12`, color:colors.secondary }}
                                                                onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.secondary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                                onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.secondary}12`; (e.currentTarget as HTMLButtonElement).style.color=colors.secondary; }}>
                                                                <Edit size={14}/>
                                                            </button>
                                                        )}
                                                        {perm.can_delete && (
                                                            <button className="usr-action-btn" onClick={()=>{ setSelectedUserForDelete(u); setIsDeleteModalOpen(true); }} title="Eliminar"
                                                                style={{ background:`${colors.primary}10`, color:colors.primary }}
                                                                onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.primary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                                onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.primary}10`; (e.currentTarget as HTMLButtonElement).style.color=colors.primary; }}>
                                                                <Trash2 size={14}/>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}

                                        {/* Empty state */}
                                        {sortedUsers.length === 0 && (
                                            <tr>
                                                <td colSpan={7} style={{ padding:'60px 24px', textAlign:'center' }}>
                                                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px' }}>
                                                        <div style={{ width:'56px', height:'56px', borderRadius:'16px', background:`${colors.primary}10`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                            <Users size={24} color={colors.primary} style={{ opacity:.5 }}/>
                                                        </div>
                                                        <p style={{ fontSize:'1rem', fontWeight:'800', color:'#0f172a' }}>No hay usuarios disponibles</p>
                                                        {perm.can_create && <p style={{ fontSize:'.82rem', color:'#94a3b8' }}>Agrega un nuevo usuario para comenzar</p>}
                                                        <div style={{ display:'flex', gap:'5px', marginTop:'4px' }}>
                                                            {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                                                <div key={i} style={{ height:'2px', width:i===0?'16px':'6px', borderRadius:'2px', background:c, opacity:.4 }}/>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {sortedUsers.length > 0 && (
                                <div style={{ borderTop:'1.5px solid #f1f5f9' }}>
                                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} itemsPerPage={itemsPerPage} totalItems={sortedUsers.length}/>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Modales */}
            <UserModal isOpen={isModalOpen} onClose={()=>setIsModalOpen(false)} isActive={isActive} toggleActive={()=>setIsActive(!isActive)} onUserCreated={handleUserCreated}/>
            {isEditModalOpen && selectedUser && <EditUserModal user={selectedUser} onClose={()=>{ setIsEditModalOpen(false); setSelectedUser(null); }} onUserUpdated={handleUserUpdated}/>}
            {isDeleteModalOpen && selectedUserForDelete && <DeleteUserModal user={selectedUserForDelete} onClose={()=>{ setIsDeleteModalOpen(false); setSelectedUserForDelete(null); }} onUserDeleted={handleUserDeleted}/>}
        </>
    );
};

export default UsersPage;